import { Dialog, Transition } from '@headlessui/react'
import { Button, Input, Select, Option } from '@material-tailwind/react'
import { useEffect, useState, Fragment } from 'react'
import * as yup from 'yup'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'
import { P } from '../../../components/typography/typography'
import statesService from '../../../Services/states.service'
import practiceService from '../../../Services/practice.services'

const CreatePracticeDialog = (props: any) => {
  const [states, setStates] = useState<{ id: number; name: string }[]>([])
  const [errorMessage, setErrorMessage] = useState('')

  const schema = yup.object().shape({
    name: yup.string().trim().required('Name is required'),
    stateId: yup.string().required('State is required'),
  })

  const { values, errors, touched, handleChange, handleSubmit, setFieldValue, resetForm, isSubmitting } = useFormik({
    initialValues: { name: '', stateId: '' },
    validationSchema: schema,
    onSubmit: (values) => createPractice(values),
  })

  useEffect(() => {
    const getStateList = async () => {
      const result = await statesService.GetStates()
      setStates(result ?? [])
    }
    getStateList()
  }, [])

  function closeModal() {
    props.stateManager()
    resetForm()
    setErrorMessage('')
  }

  const createPractice = async (values: { name: string; stateId: string }) => {
    setErrorMessage('')
    try {
      await practiceService.CreatePractice(values.name.trim(), Number(values.stateId))
      toast.success('Practice created successfully', { position: 'top-center' })
      props.GetPractices()
      closeModal()
    } catch (error: any) {
      // e.g. "The state has no questionnaire"; the dialog stays open so the admin can pick another state
      setErrorMessage(error?.response?.data?.error ?? 'Something went wrong')
    }
  }

  return (
    <div>
      <Transition appear show={props.isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <form className="mt-6" onSubmit={handleSubmit}>
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                    <Dialog.Title as="h1" className="font-medium leading-tight text-2xl mt-0 mb-2 text-black-600">
                      New Practice
                    </Dialog.Title>
                    <div className="relative p-6 flex-auto">
                      <p className="mb-4 text-slate-500">The state can't be changed once the practice is created.</p>
                      <div className="mb-4">
                        <Input label="Name" name="name" value={values.name} onChange={handleChange} />
                        {touched.name && errors.name && <P className="text-red-600">{errors.name}</P>}
                      </div>
                      <div className="mb-4">
                        <Select label="State" value={values.stateId} onChange={(value) => setFieldValue('stateId', value ?? '')}>
                          {states.map((state) => (
                            <Option key={state.id} value={String(state.id)}>
                              {state.name}
                            </Option>
                          ))}
                        </Select>
                        {touched.stateId && errors.stateId && <P className="text-red-600">{errors.stateId}</P>}
                      </div>
                      {errorMessage && <P className="text-red-600">{errorMessage}</P>}
                    </div>

                    <div className="flex items-center justify-end p-6 border-t border-solid border-slate-200 rounded-b">
                      <button
                        className="text-blue-500 background-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                        type="button"
                        onClick={closeModal}
                      >
                        Cancel
                      </button>
                      <Button type="submit" disabled={isSubmitting}>
                        Create Practice
                      </Button>
                    </div>
                  </Dialog.Panel>
                </form>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  )
}

export default CreatePracticeDialog
